// Defines the safety-preserving registry boundary from runtime validation
// through typed compound reduction and projected transcript rows.
import { type ReactNode } from 'react';
import { type z } from 'zod';

import { type EhrPersonNoteEditor } from '@apps/web/features/phoebe_chat_v3/model/ehr_person_note_editor';
import {
    type HarnessScope,
    type KnownEventType,
    type RawCommittedEvent,
    type RawPendingEvent,
    type Uuid,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { type UiContextSnapshot } from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import {
    type QuestionAnswerRequest,
    type ToolDecisionRequest,
} from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import { type OutreachCardHeading } from '@apps/web/features/phoebe_chat_v3/model/outreach_card_status';
import { type SettingsApprovalEditor } from '@apps/web/features/phoebe_chat_v3/model/settings_approval';
import {
    type ShiftWriteCard,
    type ShiftWriteEditor,
} from '@apps/web/features/phoebe_chat_v3/model/shift_write_card';
import { type V3ChatAttachment } from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_card';

export type OccurrencePhase =
    | 'committed'
    | 'pending'
    | 'reconciling'
    | 'rejected'
    | 'optimistic';

export type InteractionSubmitOutcome =
    | 'accepted'
    | 'already_resolved'
    | 'rejected'
    | 'ambiguous'
    | 'ignored';

export type RegistryOccurrence<K extends KnownEventType> = Readonly<{
    readonly occurrenceId: string;
    readonly type: K;
    readonly phase: OccurrencePhase;
    readonly committed: RawCommittedEvent | null;
    readonly pending: RawPendingEvent | null;
}>;

export type SafeRenderContext = Readonly<{
    readonly scope: HarnessScope;
    readonly nowMs: number;
    readonly organizationTimeZone: string;
    /** Person-name display order for the chat's organization. */
    readonly formatPersonName: (name: string | null | undefined) => string;
    readonly isExpanded: boolean;
    /** Saved outreaches linked to this chat, excluding unrelated shift history. */
    readonly tableOutreaches?: ReadonlyArray<
        Readonly<{ outreachId: string; sourceEventId: string }>
    >;
    readonly requestGroupToggle: (groupId: string) => void;
    readonly requestRetry: (occurrenceId: string) => void;
    readonly requestQuestionSubmit: (
        callId: string,
        answer: QuestionAnswerRequest,
    ) => Promise<InteractionSubmitOutcome>;
    readonly requestToolDecision: (
        request: ToolDecisionRequest,
    ) => Promise<InteractionSubmitOutcome>;
    /** Sends a message on the reader's behalf, as if typed in the composer. */
    readonly requestSendMessage: (content: string) => void;
    /** In-app navigation for cards that hand the reader to another page. */
    readonly requestNavigate: (href: string) => void;
}>;

export type MalformedPolicy =
    | Readonly<{
          readonly kind: 'placeholder';
          readonly customerCopy: string;
      }>
    | Readonly<{ readonly kind: 'skip' }>;

export type GroupPhase = 'active' | 'completed' | 'failed';

export type ActiveWorkMetadata =
    | Readonly<{ readonly kind: 'generic' }>
    | Readonly<{
          readonly kind: 'check_in';
          readonly scheduledAtMs: number;
          readonly fireAtMs: number;
      }>;

export type ActivityGroupDeclaration<P, C> = Readonly<{
    readonly kind: string;
    readonly boundary: 'consecutive_same_kind';
    readonly phase: (
        payload: P,
        compoundState: C,
        occurrence: RegistryOccurrence<KnownEventType>,
        context: SafeRenderContext,
    ) => GroupPhase;
    readonly presentSummary: (payload: P, compoundState: C) => string;
    readonly pastSummary: (payload: P, compoundState: C) => string;
    readonly activeWork?: (
        payload: P,
        compoundState: C,
        occurrence: RegistryOccurrence<KnownEventType>,
    ) => ActiveWorkMetadata;
    /**
     * A receipt stays in the transcript as its own row in every phase. The
     * group declaration then only describes its live work (stack row, stop
     * eligibility); it never folds into a consecutive activity group.
     */
    readonly receipt?: true;
}>;

export type OwnerActivityGroupDeclaration<P, C> = ActivityGroupDeclaration<
    P,
    C
> &
    Readonly<{
        /**
         * Whether this occurrence renders a card the reader has to see. Like
         * an approval, question or result presentation, it keeps its own row
         * instead of folding into the activity group.
         */
        readonly carriesCard?: (payload: P, compoundState: C) => boolean;
    }>;

export type ResolvedActivityGroup<C> = Readonly<{
    readonly kind: string;
    readonly boundary: 'consecutive_same_kind';
    readonly phase: GroupPhase;
    readonly presentSummary: string;
    readonly pastSummary: string;
    readonly activeWork: ActiveWorkMetadata;
    readonly compoundState: C;
    readonly receipt?: true;
    readonly carriesCard?: true;
}>;

export type RegistryRuntimeIssue = Readonly<{
    readonly reason: 'missing_customer_narration';
}>;

export type SlotKey =
    | Readonly<{ readonly kind: 'call_id'; readonly value: string }>
    | Readonly<{ readonly kind: 'event_id'; readonly value: Uuid }>
    | Readonly<{ readonly kind: 'command_id'; readonly value: Uuid }>;

export type SlotCardinality = 'one' | 'many';

export const SELECTABLE_ID_COLUMNS = [
    'shift_id',
    'caregiver_id',
    'client_id',
] as const;

export type SelectableIdColumn = (typeof SELECTABLE_ID_COLUMNS)[number];

export type TableSelectionAction = Readonly<{
    readonly label: string;
    readonly prompt: string;
}>;

export type GeneratedTablePresentation = Readonly<{
    readonly kind: 'table';
    readonly uiContextReceipt?: Omit<UiContextSnapshot['receipt'], 'run_id'>;
    readonly title: string | null;
    readonly workspacePath: string;
    readonly format: 'csv' | 'jsonl';
    readonly rowCount: number;
    readonly columns: ReadonlyArray<string>;
    readonly columnsTruncated: boolean;
    readonly sourceEventId: Uuid;
    /** Rows the reader can pick, and what they can do with the picked ones. */
    readonly selection?: Readonly<{
        readonly idColumn: SelectableIdColumn;
        readonly actions: ReadonlyArray<TableSelectionAction>;
    }>;
}>;

export type AgencyRulesPresentation = Readonly<{
    readonly kind: 'agency_rules';
    readonly workspacePath: string;
    readonly rowCount: number;
    readonly sourceEventId: Uuid;
}>;

export type NextStepAction = Readonly<{
    readonly label: string;
    readonly prompt: string;
}>;

/** Post-answer suggestions: a click sends the prompt as the reader's own. */
export type NextStepsPresentation = Readonly<{
    readonly kind: 'next_steps';
    readonly actions: ReadonlyArray<NextStepAction>;
}>;

/** Curated completion copy derived from a validated write result. */
export type WriteResultPresentation = Readonly<{
    readonly kind: 'write_receipt';
    readonly status?: 'succeeded' | 'failed';
    readonly shiftCard?: ShiftWriteCard;
    readonly settingsChanges?: Readonly<
        Record<
            string,
            Readonly<{
                from: boolean | number | null;
                to: boolean | number | null;
            }>
        >
    >;
    readonly title: string;
    readonly summary?: string;
    readonly notices: ReadonlyArray<string>;
}>;

export type ToolResultPresentation =
    | Readonly<{
          kind: 'outreach';
          outreachId: string;
          heading: OutreachCardHeading;
      }>
    | Readonly<{ kind: 'recommendations'; outreachId: string }>
    | GeneratedTablePresentation
    | AgencyRulesPresentation
    | NextStepsPresentation
    | WriteResultPresentation
    | Omit<V3ChatAttachment, 'runId'>;

export type AssignmentResult = Readonly<{
    readonly notices?: ReadonlyArray<string>;
    readonly status: 'assigned' | 'partial' | 'needs_confirmation' | 'rejected';
    readonly shifts: ReadonlyArray<
        Readonly<{
            readonly id: string;
            readonly assigned: boolean;
        }>
    >;
}>;
export type CommunicationOutcome =
    | 'succeeded'
    | 'partial'
    | 'no_contact'
    | 'interrupted';

export type NoteResult = Readonly<{
    readonly status: 'saved' | 'partial' | 'not_saved';
    readonly details: ReadonlyArray<string>;
}>;

export type ToolResultFragment =
    | Readonly<{
          readonly kind: 'tool_succeeded';
          readonly pastNarration: string;
          readonly presentation?: ToolResultPresentation;
          readonly questionAnswer?: string | ReadonlyArray<string>;
          readonly assignmentResult?: AssignmentResult;
          readonly communicationOutcome?: CommunicationOutcome;
          readonly noteResult?: NoteResult;
      }>
    | Readonly<{
          readonly kind: 'tool_failed';
          readonly pastNarration: string;
          readonly presentation?: ToolResultPresentation;
      }>;

export type ApprovalPresentation = Readonly<{
    readonly question: string;
    readonly groups: ReadonlyArray<
        Readonly<{
            readonly heading: string;
            readonly items: ReadonlyArray<string>;
        }>
    >;
}>;

export type CaregiverTemplateValues = Readonly<{
    readonly caregiver_first_name: string;
    readonly caregiver_last_name: string;
    readonly caregiver_name: string;
}>;

export type ApprovalEditor =
    | SettingsApprovalEditor
    | ShiftWriteEditor
    | EhrPersonNoteEditor
    | Readonly<{
          readonly kind: 'sms';
          readonly recipients: ReadonlyArray<
              Readonly<{
                  readonly handle_type: 'caregiver_id' | 'conversation_id';
                  readonly handle_id: Uuid;
                  readonly name: string;
                  readonly message: string;
                  readonly template_values?: CaregiverTemplateValues;
              }>
          >;
      }>
    | Readonly<{
          readonly kind: 'voice';
          readonly handle_type: 'caregiver_id' | 'conversation_id';
          readonly handle_id: Uuid;
          readonly name: string;
          readonly template_values?: CaregiverTemplateValues;
          readonly instructions: string;
          readonly opening_line: string | null;
      }>
    | Readonly<{
          readonly kind: 'assign_fill';
          readonly caregiver_id: Uuid;
          readonly caregiver_name: string;
          readonly shifts: ReadonlyArray<
              Readonly<{
                  readonly id: Uuid;
                  readonly name: string;
                  readonly detail: string;
                  readonly recurrence_label: string | null;
              }>
          >;
          readonly notices?: ReadonlyArray<
              Readonly<{
                  readonly text: string;
                  readonly shift_ids: ReadonlyArray<Uuid> | null;
              }>
          >;
      }>
    | Readonly<{
          readonly kind: 'save_note';
      }>
    | Readonly<{
          readonly kind: 'outreach';
          readonly shift_count: number;
          readonly client_count: number;
          readonly date_range: string;
      }>;

export type PermissionOutcome = 'pending' | 'succeeded' | 'failed';

export type ApprovalFragment =
    | Readonly<{
          readonly kind: 'approval_required';
          // Renderer-owned capability; the shell never branches on card kind.
          readonly statusPlacement?: 'inline' | 'shell';
          readonly commandId: Uuid;
          readonly presentation: ApprovalPresentation | null;
          readonly editor?: ApprovalEditor | null;
          // Server-decided: false when an always-allow grant would not
          // persist (or would be rejected) for this tool, so the card must
          // not offer it. Tool identity never crosses this boundary.
          readonly alwaysAllowAvailable: boolean;
      }>
    | Readonly<{
          readonly kind: 'approval_resolved';
          readonly commandId: Uuid;
          readonly decision: 'granted' | 'denied' | 'expired';
          readonly presentation?: ApprovalPresentation | null;
          readonly editor?: ApprovalEditor | null;
      }>;

export type QuestionFragment =
    | Readonly<{
          readonly kind: 'question_required';
          readonly commandId: Uuid;
      }>
    | Readonly<{
          readonly kind: 'question_resolved';
          readonly commandId: Uuid;
          readonly resolution: 'answered' | 'cancelled' | 'expired';
          readonly answer: string | null;
      }>;

export type ArtifactFragment = Readonly<{
    readonly kind: 'artifact_updated';
    readonly sourceEventId: Uuid;
}>;

export type CompoundSlotContracts = Readonly<{
    readonly tool_result: ToolResultFragment;
    readonly approval: ApprovalFragment;
    readonly question: QuestionFragment;
    readonly artifacts: ArtifactFragment;
}>;

export type ToolCompoundState = Readonly<{
    readonly result: ToolResultFragment | null;
    readonly approval: ApprovalFragment | null;
    readonly question: QuestionFragment | null;
    readonly artifacts: ReadonlyArray<ArtifactFragment>;
    readonly appliedMemberOccurrenceIds: ReadonlySet<string>;
}>;

export type CompoundSlotName = keyof CompoundSlotContracts;

export type CompoundSlotDeclaration<C, N extends CompoundSlotName> = Readonly<{
    readonly name: N;
    readonly cardinality: SlotCardinality;
    readonly reduce: (
        state: C,
        fragment: CompoundSlotContracts[N],
        memberOccurrenceId: string,
    ) => C;
}>;

export type AnyCompoundSlotDeclaration<C> = {
    readonly [N in CompoundSlotName]: CompoundSlotDeclaration<C, N>;
}[CompoundSlotName];

export type SlotMemberDeclaration<P, N extends CompoundSlotName> = Readonly<{
    readonly slot: N;
    readonly terminal: boolean;
    readonly keys: (
        payload: P,
        occurrence: RegistryOccurrence<KnownEventType>,
    ) => ReadonlyArray<SlotKey>;
    readonly fragment: (
        payload: P,
        occurrence: RegistryOccurrence<KnownEventType>,
        slot: N,
    ) => CompoundSlotContracts[N];
}>;

export type AnySlotMemberDeclaration<P> = {
    readonly [N in CompoundSlotName]: SlotMemberDeclaration<P, N>;
}[CompoundSlotName];

export type ParsedSlotMember<N extends CompoundSlotName> = Readonly<{
    readonly slot: N;
    readonly terminal: boolean;
    readonly keys: ReadonlyArray<SlotKey>;
    readonly fragment: CompoundSlotContracts[N];
    readonly memberOccurrenceId: string;
}>;

export type AnyParsedSlotMember = {
    readonly [N in CompoundSlotName]: ParsedSlotMember<N>;
}[CompoundSlotName];

export type ParsedMemberOfDeclaration<D> =
    D extends SlotMemberDeclaration<infer _P, infer N>
        ? ParsedSlotMember<N>
        : never;

export function materializeSlotMember<P, D extends AnySlotMemberDeclaration<P>>(
    declaration: D,
    payload: P,
    occurrence: RegistryOccurrence<KnownEventType>,
): ParsedMemberOfDeclaration<D>;

export function materializeSlotMember<P, N extends CompoundSlotName>(
    declaration: SlotMemberDeclaration<P, N>,
    payload: P,
    occurrence: RegistryOccurrence<KnownEventType>,
): ParsedSlotMember<N>;

export function materializeSlotMember<P, N extends CompoundSlotName>(
    declaration: SlotMemberDeclaration<P, N>,
    payload: P,
    occurrence: RegistryOccurrence<KnownEventType>,
): ParsedSlotMember<N> {
    return {
        slot: declaration.slot,
        terminal: declaration.terminal,
        keys: declaration.keys(payload, occurrence),
        fragment: declaration.fragment(payload, occurrence, declaration.slot),
        memberOccurrenceId: occurrence.occurrenceId,
    };
}

export function reduceParsedSlotMember<C, N extends CompoundSlotName>(
    state: C,
    ownerSlot: CompoundSlotDeclaration<C, N>,
    member: ParsedSlotMember<N>,
): C {
    return ownerSlot.reduce(state, member.fragment, member.memberOccurrenceId);
}

export type LifecycleEvidenceDelta =
    | Readonly<{
          readonly kind: 'run_started';
          readonly eventId: Uuid;
          readonly occurredAtMs: number;
      }>
    | Readonly<{
          readonly kind: 'run_terminal';
          readonly eventId: Uuid;
          readonly occurredAtMs: number;
      }>
    | Readonly<{
          readonly kind: 'interrupt_requested';
          readonly occurredAtMs: number;
      }>;

export type EvidenceDeclaration<P> = (
    payload: P,
    occurrence: RegistryOccurrence<KnownEventType>,
) => LifecycleEvidenceDelta | null;

export type RenderMaterialization<C> =
    | Readonly<{
          readonly kind: 'rendered';
          readonly node: ReactNode;
          readonly evidence: LifecycleEvidenceDelta | null;
          readonly group: ResolvedActivityGroup<C> | null;
          readonly issues: ReadonlyArray<RegistryRuntimeIssue>;
      }>
    | Readonly<{
          readonly kind: 'malformed';
          readonly policy: MalformedPolicy;
      }>;

export type MemberMaterialization<N extends CompoundSlotName> =
    | Readonly<{
          readonly kind: 'member';
          readonly member: ParsedSlotMember<N>;
          readonly evidence: LifecycleEvidenceDelta | null;
          readonly issues: ReadonlyArray<RegistryRuntimeIssue>;
      }>
    | Readonly<{
          readonly kind: 'malformed';
          readonly policy: MalformedPolicy;
      }>;

export type AnyMemberMaterialization = {
    readonly [N in CompoundSlotName]: MemberMaterialization<N>;
}[CompoundSlotName];

export type InvisibleMaterialization =
    | Readonly<{
          readonly kind: 'invisible';
          readonly evidence: LifecycleEvidenceDelta | null;
          readonly issues: ReadonlyArray<RegistryRuntimeIssue>;
      }>
    | Readonly<{
          readonly kind: 'malformed';
          readonly policy: Readonly<{ readonly kind: 'skip' }>;
      }>;

type RegistryIssueDeclaration<P, C> = (
    payload: P,
    compoundState: C,
) => ReadonlyArray<RegistryRuntimeIssue>;

type StandaloneDefinitionFields<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
> = Readonly<{
    readonly disposition: 'render';
    readonly schema: S;
    readonly malformed: MalformedPolicy;
    readonly evidence: EvidenceDeclaration<z.output<S>> | null;
    readonly group: ActivityGroupDeclaration<z.output<S>, null> | null;
    readonly issues?: RegistryIssueDeclaration<z.output<S>, null>;
    readonly render: (
        payload: z.output<S>,
        occurrence: RegistryOccurrence<K>,
        context: SafeRenderContext,
    ) => ReactNode;
}>;

export type StandaloneDefinition<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
> = StandaloneDefinitionFields<K, S> &
    Readonly<{
        readonly materialize: (
            input: unknown,
            occurrence: RegistryOccurrence<K>,
            context: SafeRenderContext,
        ) => RenderMaterialization<null>;
    }>;

type CompoundOwnerDefinitionFields<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
    C,
> = Readonly<{
    readonly disposition: 'compound_owner';
    readonly schema: S;
    readonly malformed: MalformedPolicy;
    readonly evidence: EvidenceDeclaration<z.output<S>> | null;
    readonly keys: (
        payload: z.output<S>,
        occurrence: RegistryOccurrence<K>,
    ) => ReadonlyArray<SlotKey>;
    readonly initialState: C;
    readonly slots: ReadonlyArray<AnyCompoundSlotDeclaration<C>>;
    readonly group: OwnerActivityGroupDeclaration<z.output<S>, C> | null;
    readonly issues?: RegistryIssueDeclaration<z.output<S>, C>;
    readonly render: (
        payload: z.output<S>,
        compoundState: C,
        occurrence: RegistryOccurrence<K>,
        context: SafeRenderContext,
    ) => ReactNode;
}>;

export type CompoundOwnerDefinition<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
    C,
> = CompoundOwnerDefinitionFields<K, S, C> &
    Readonly<{
        readonly materialize: (
            input: unknown,
            compoundState: C,
            occurrence: RegistryOccurrence<K>,
            context: SafeRenderContext,
        ) => RenderMaterialization<C>;
    }>;

type CompoundMemberDefinitionFields<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
    N extends CompoundSlotName,
> = Readonly<{
    readonly disposition: 'compound_member';
    readonly schema: S;
    readonly malformed: MalformedPolicy;
    readonly evidence: EvidenceDeclaration<z.output<S>> | null;
    readonly issues?: RegistryIssueDeclaration<z.output<S>, null>;
    readonly slotMember: SlotMemberDeclaration<z.output<S>, N>;
}>;

export type CompoundMemberDefinition<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
    N extends CompoundSlotName,
> = CompoundMemberDefinitionFields<K, S, N> &
    Readonly<{
        readonly materialize: (
            input: unknown,
            occurrence: RegistryOccurrence<K>,
        ) => MemberMaterialization<N>;
    }>;

export type AnyCompoundMemberDefinition<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
> = {
    readonly [N in CompoundSlotName]: CompoundMemberDefinition<K, S, N>;
}[CompoundSlotName];

export type InvisibleDefinition<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
> = Readonly<{
    readonly disposition: 'invisible';
    readonly schema: S;
    readonly malformed: Readonly<{ readonly kind: 'skip' }>;
    readonly evidence: EvidenceDeclaration<z.output<S>> | null;
    readonly materialize: (
        input: unknown,
        occurrence: RegistryOccurrence<K>,
    ) => InvisibleMaterialization;
}>;

type InvisibleDefinitionFields<S extends z.ZodTypeAny> = Readonly<{
    readonly disposition: 'invisible';
    readonly schema: S;
    readonly malformed: Readonly<{ readonly kind: 'skip' }>;
    readonly evidence: EvidenceDeclaration<z.output<S>> | null;
}>;

export type RegistryDefinitionShape = Readonly<{
    readonly disposition:
        | 'render'
        | 'compound_owner'
        | 'compound_member'
        | 'invisible';
    readonly schema: z.ZodType;
    readonly malformed: MalformedPolicy;
}>;

export type RendererRegistryShape = Readonly<{
    readonly [K in KnownEventType]: RegistryDefinitionShape;
}>;

export type RendererRegistry<R extends RendererRegistryShape> = Readonly<R>;

export type RendererOverlay<R extends RendererRegistryShape> = Readonly<
    Partial<{
        readonly [K in KnownEventType]: R[K];
    }>
>;

export function defineStandalone<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
>(
    _type: K,
    definition: StandaloneDefinitionFields<K, S>,
): StandaloneDefinition<K, S> {
    return {
        ...definition,
        materialize(input, occurrence, context) {
            const parsed = definition.schema.safeParse(input);
            if (!parsed.success) {
                return { kind: 'malformed', policy: definition.malformed };
            }
            const compoundState = null;
            const declaredPhase = definition.group?.phase(
                parsed.data,
                compoundState,
                occurrence,
                context,
            );
            return {
                kind: 'rendered',
                node: definition.render(parsed.data, occurrence, context),
                evidence:
                    definition.evidence?.(parsed.data, occurrence) ?? null,
                group:
                    definition.group === null || declaredPhase === undefined
                        ? null
                        : {
                              kind: definition.group.kind,
                              boundary: definition.group.boundary,
                              phase: declaredPhase,
                              presentSummary: definition.group.presentSummary(
                                  parsed.data,
                                  compoundState,
                              ),
                              pastSummary: definition.group.pastSummary(
                                  parsed.data,
                                  compoundState,
                              ),
                              activeWork: definition.group.activeWork?.(
                                  parsed.data,
                                  compoundState,
                                  occurrence,
                              ) ?? { kind: 'generic' },
                              compoundState,
                              ...(definition.group.receipt === true
                                  ? { receipt: true as const }
                                  : {}),
                          },
                issues: definition.issues?.(parsed.data, compoundState) ?? [],
            };
        },
    };
}

export function defineCompoundOwner<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
    C,
>(
    _type: K,
    definition: CompoundOwnerDefinitionFields<K, S, C>,
): CompoundOwnerDefinition<K, S, C> {
    return {
        ...definition,
        materialize(input, compoundState, occurrence, context) {
            const parsed = definition.schema.safeParse(input);
            if (!parsed.success) {
                return { kind: 'malformed', policy: definition.malformed };
            }
            return {
                kind: 'rendered',
                node: definition.render(
                    parsed.data,
                    compoundState,
                    occurrence,
                    context,
                ),
                evidence:
                    definition.evidence?.(parsed.data, occurrence) ?? null,
                group:
                    definition.group === null
                        ? null
                        : {
                              kind: definition.group.kind,
                              boundary: definition.group.boundary,
                              phase: definition.group.phase(
                                  parsed.data,
                                  compoundState,
                                  occurrence,
                                  context,
                              ),
                              presentSummary: definition.group.presentSummary(
                                  parsed.data,
                                  compoundState,
                              ),
                              pastSummary: definition.group.pastSummary(
                                  parsed.data,
                                  compoundState,
                              ),
                              activeWork: definition.group.activeWork?.(
                                  parsed.data,
                                  compoundState,
                                  occurrence,
                              ) ?? { kind: 'generic' },
                              compoundState,
                              ...(definition.group.receipt === true
                                  ? { receipt: true as const }
                                  : {}),
                              ...(definition.group.carriesCard?.(
                                  parsed.data,
                                  compoundState,
                              ) === true
                                  ? { carriesCard: true as const }
                                  : {}),
                          },
                issues: definition.issues?.(parsed.data, compoundState) ?? [],
            };
        },
    };
}

export function defineCompoundMember<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
    N extends CompoundSlotName,
>(
    _type: K,
    definition: CompoundMemberDefinitionFields<K, S, N>,
): CompoundMemberDefinition<K, S, N> {
    return {
        ...definition,
        materialize(input, occurrence) {
            const parsed = definition.schema.safeParse(input);
            if (!parsed.success) {
                return { kind: 'malformed', policy: definition.malformed };
            }
            return {
                kind: 'member',
                member: materializeSlotMember(
                    definition.slotMember,
                    parsed.data,
                    occurrence,
                ),
                evidence:
                    definition.evidence?.(parsed.data, occurrence) ?? null,
                issues: definition.issues?.(parsed.data, null) ?? [],
            };
        },
    };
}

export function defineInvisible<
    K extends KnownEventType,
    S extends z.ZodTypeAny,
>(
    _type: K,
    definition: InvisibleDefinitionFields<S>,
): InvisibleDefinition<K, S> {
    return {
        ...definition,
        materialize(input, occurrence) {
            const parsed = definition.schema.safeParse(input);
            if (!parsed.success) {
                return { kind: 'malformed', policy: definition.malformed };
            }
            return {
                kind: 'invisible',
                evidence:
                    definition.evidence?.(parsed.data, occurrence) ?? null,
                issues: [],
            };
        },
    };
}

/**
 * Who a plain chat message is from, for grouping one sender's run of them.
 * People are keyed by user id so two teammates' messages never merge.
 */
export type MessageSender = 'assistant' | `user:${string}`;

export type ProjectedTranscriptRow =
    | Readonly<{
          readonly kind: 'day_heading';
          readonly rowId: string;
          readonly day: string;
      }>
    | Readonly<{
          readonly kind: 'occurrence';
          readonly rowId: string;
          readonly occurrenceId: string;
          readonly eventIds: ReadonlyArray<Uuid>;
          readonly node: ReactNode;
          readonly messageSender?: MessageSender;
          readonly messageText?: string;
          readonly isCard?: boolean;
      }>
    | Readonly<{
          readonly kind: 'activity_group_summary';
          readonly rowId: string;
          readonly groupId: string;
          readonly eventIds: ReadonlyArray<Uuid>;
          readonly expanded: boolean;
          readonly summary: string;
          readonly memberCount: number;
          readonly startedAtMs: number | null;
          readonly live: boolean;
      }>
    | Readonly<{
          readonly kind: 'activity_group_member';
          readonly rowId: string;
          readonly groupId: string;
          readonly occurrenceId: string;
          readonly eventIds: ReadonlyArray<Uuid>;
          readonly node: ReactNode;
      }>
    | Readonly<{
          readonly kind: 'placeholder';
          readonly rowId: string;
          readonly occurrenceId: string;
          readonly customerCopy: string;
      }>;
